import React, { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../components/table";
import type { DataTableSortDirection } from "../../../../components/table";
import type { SurveyListFilters } from "../../hooks/useGetSurveyList";
import type { SurveyListItemForAdminResponse, SurveyStatus } from "../../types/survey.types";
import { buildSurveyListColumns } from "./surveyListTableColumns";

type SurveyTableProps = {
  surveys: SurveyListItemForAdminResponse[];
  filters: SurveyListFilters;
  setFilters: Dispatch<SetStateAction<SurveyListFilters>>;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick: (survey: SurveyListItemForAdminResponse) => void;
  onEditClick: (survey: SurveyListItemForAdminResponse) => void;
  onDeleteClick: (survey: SurveyListItemForAdminResponse) => void;
  getSurveyStatus: (status: SurveyStatus) => string;
  sortDirections: Partial<Record<"points" | "question_count" | "responses_count", DataTableSortDirection | null>>;
  onColumnSort: (
    columnName: "points" | "question_count" | "responses_count",
    nextDirection: DataTableSortDirection | null,
  ) => void;
};

const SurveyTable: React.FC<SurveyTableProps> = ({
  surveys,
  filters,
  setFilters,
  loading = false,
  error = null,
  onRetry,
  page,
  totalPages,
  onPageChange,
  onRowClick,
  onEditClick,
  onDeleteClick,
  getSurveyStatus,
  sortDirections,
  onColumnSort,
}) => {
  const { t } = useTranslation();

  const updateFilter = useCallback(
    <K extends keyof SurveyListFilters>(key: K, value: SurveyListFilters[K] | null) => {
      setFilters((prev: SurveyListFilters) => {
        if (value === null) {
          const nextFilters = { ...prev };
          delete nextFilters[key];
          return nextFilters;
        }

        return {
          ...prev,
          [key]: value,
        };
      });
    },
    [setFilters],
  );

  const columns = useMemo(
    () =>
      buildSurveyListColumns({
        t,
        filters,
        updateFilter,
        onEdit: onEditClick,
        onDelete: onDeleteClick,
        getSurveyStatus,
        sortDirections,
        onColumnSort,
      }),
    [
      t,
      filters,
      updateFilter,
      onEditClick,
      onDeleteClick,
      getSurveyStatus,
      sortDirections,
      onColumnSort,
    ],
  );

  return (
    <div className="relative">
      <DataTable
        columns={columns}
        dataSource={surveys}
        rowKey="id"
        emptyLabel={null}
        loading={loading}
        loadingLabel={t("survey.loading_surveys")}
        error={error}
        onRetry={onRetry}
          containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
        tableClassName="min-w-max"
        onRowClick={(survey) => onRowClick(survey)}
        pagination={{
          page,
          totalPages,
          onPageChange,
        }}
      />
    </div>
  );
};

export default SurveyTable;
